import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MessageCircle, Mail, Share2, Link2, X } from 'lucide-react';
import { clsx } from 'clsx';
import { useTheme } from '../../contexts/ThemeContext';
import { isWebShareSupported, prepareWhatsAppWindow } from '../../lib/vitrina-queries';

export type ShareAction = 'whatsapp' | 'email' | 'system' | 'copy';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Called with the chosen channel + the hideContact toggle value. Caller is
   * responsible for the actual share (create_showcase + open destination).
   * The dialog passes back `waWindow` only for the WhatsApp action so iOS
   * Safari's user-gesture window survives the async create_showcase call.
   */
  onSelect: (action: ShareAction, hideContact: boolean, waWindow: Window | null) => void;
  title?: string;
  defaultHideContact?: boolean;
  lockContact?: boolean;
}

export const ShareOptionsDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  onSelect,
  title = 'Compartir propiedad',
  defaultHideContact = false,
  lockContact = false,
}) => {
  const { theme } = useTheme();
  const isLight = theme !== 'dark';
  const [hideContact, setHideContact] = useState(defaultHideContact);

  useEffect(() => {
    if (isOpen) setHideContact(defaultHideContact);
  }, [isOpen, defaultHideContact]);

  if (!isOpen) return null;

  const handlePick = (action: ShareAction) => {
    // For WhatsApp we MUST open the placeholder window synchronously inside
    // the click handler — see prepareWhatsAppWindow docs. For others it's a
    // no-op (`null`).
    const waWindow = action === 'whatsapp' ? prepareWhatsAppWindow() : null;
    onClose();
    onSelect(action, hideContact, waWindow);
  };

  const options: Array<{
    action: ShareAction;
    label: string;
    sub: string;
    Icon: typeof MessageCircle;
    iconBg: string;
    iconColor: string;
    show: boolean;
  }> = [
    {
      action: 'whatsapp',
      label: 'WhatsApp',
      sub: 'Enviar por WhatsApp',
      Icon: MessageCircle,
      iconBg: isLight ? 'bg-emerald-50' : 'bg-emerald-500/15',
      iconColor: 'text-emerald-600',
      show: true,
    },
    {
      action: 'email',
      label: 'Email',
      sub: 'Enviar por correo',
      Icon: Mail,
      iconBg: isLight ? 'bg-sky-50' : 'bg-sky-500/15',
      iconColor: isLight ? 'text-sky-600' : 'text-sky-400',
      show: true,
    },
    {
      action: 'system',
      label: 'Más opciones',
      sub: 'Telegram, Messenger, AirDrop…',
      Icon: Share2,
      iconBg: isLight ? 'bg-indigo-50' : 'bg-indigo-500/15',
      iconColor: isLight ? 'text-indigo-600' : 'text-indigo-400',
      show: isWebShareSupported(),
    },
    {
      action: 'copy',
      label: 'Copiar vínculo',
      sub: 'Pega donde quieras',
      Icon: Link2,
      iconBg: isLight ? 'bg-slate-100' : 'bg-white/10',
      iconColor: isLight ? 'text-slate-600' : 'text-slate-300',
      show: true,
    },
  ];

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div
        className={clsx(
          'relative w-full max-w-md rounded-2xl border shadow-2xl',
          isLight ? 'bg-white border-slate-200' : 'bg-slate-900 border-slate-700',
        )}
      >
        <div className={clsx('p-5 border-b flex items-center justify-between gap-3', isLight ? 'border-slate-100' : 'border-white/5')}>
          <div className="flex-1 min-w-0">
            <h3 className={clsx('text-base font-bold', isLight ? 'text-slate-900' : 'text-white')}>{title}</h3>
            <p className={clsx('text-xs mt-0.5', isLight ? 'text-slate-500' : 'text-slate-400')}>
              Elige cómo enviar el vínculo a tu cliente.
            </p>
          </div>
          <button
            onClick={onClose}
            className={clsx('p-1.5 rounded-lg', isLight ? 'text-slate-400 hover:text-slate-600 hover:bg-slate-100' : 'text-slate-500 hover:text-slate-300 hover:bg-white/5')}
          >
            <X size={18} />
          </button>
        </div>

        {!lockContact && (
          <div className={clsx('px-5 pt-4')}>
            <label
              className={clsx(
                'flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors',
                isLight ? 'border-slate-200 hover:bg-slate-50' : 'border-white/10 hover:bg-white/5',
              )}
            >
              <input
                type="checkbox"
                checked={!hideContact}
                onChange={(e) => setHideContact(!e.target.checked)}
                className="w-4 h-4 accent-indigo-600"
              />
              <div className="flex-1 min-w-0">
                <div className={clsx('text-sm font-semibold', isLight ? 'text-slate-900' : 'text-white')}>Incluir mis datos</div>
                <div className={clsx('text-xs mt-0.5', isLight ? 'text-slate-500' : 'text-slate-400')}>
                  {hideContact
                    ? 'El cliente no verá tu nombre, teléfono ni correo.'
                    : 'Aparecerás como contacto en la vitrina.'}
                </div>
              </div>
            </label>
          </div>
        )}

        <div className="p-3">
          {options.filter((o) => o.show).map(({ action, label, sub, Icon, iconBg, iconColor }) => (
            <button
              key={action}
              onClick={() => handlePick(action)}
              className={clsx(
                'w-full flex items-center gap-3 p-3 rounded-xl transition-colors text-left',
                isLight ? 'hover:bg-slate-100' : 'hover:bg-white/5',
              )}
            >
              <div className={clsx('w-10 h-10 rounded-xl flex items-center justify-center shrink-0', iconBg)}>
                <Icon size={18} className={iconColor} />
              </div>
              <div className="flex-1 min-w-0">
                <div className={clsx('text-sm font-semibold', isLight ? 'text-slate-900' : 'text-white')}>{label}</div>
                <div className={clsx('text-xs mt-0.5', isLight ? 'text-slate-500' : 'text-slate-400')}>{sub}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
};
